import { productPricingConfigKeys } from '../calculator/product-pricing-configs';
import { resolveBillingCycle } from '../calculator/resolve-billing-cycle';
import { fromQueryString, ParsedQuery } from '../util/url';

import { EDITION_KEYS, STEP_ORDER } from './constants';
import { buildDefaultProduct, UbpInitialValues } from './context';

import type { Meter, Product, Step, UsageType } from './types';
import type { Edition, ProductKey } from '../calculator/types';

const STEP_VALUES: ReadonlySet<Step> = new Set<Step>(STEP_ORDER);

const METER_VALUES: ReadonlySet<Meter> = new Set<Meter>([
	'rovoCredits',
	'automations',
	'csResolutions',
	'assetsObjects',
]);

const USAGE_TYPE_VALUES: ReadonlySet<UsageType> = new Set<UsageType>([
	'exploring',
	'adopting',
	'scaling',
]);

/**
 * Parse a query string into a `Partial<UbpInitialValues>` suitable for the
 * provider's `initialValues` prop. Unknown/invalid values are dropped.
 * Inverse of `selectShareableQuery`.
 */
export const initialValuesFromQuery = (search: string): Partial<UbpInitialValues> => {
	const parsed = fromQueryString(search);
	const initial: Partial<UbpInitialValues> = {};

	const step = asEnum<Step>(parsed.scalars['step'], STEP_VALUES);
	if (step) initial.step = step;

	const meter = asEnum<Meter>(parsed.scalars['meter'], METER_VALUES);
	if (meter) initial.meter = meter;

	const usageType = asEnum<UsageType>(parsed.scalars['usageType'], USAGE_TYPE_VALUES);
	if (usageType) initial.usageType = usageType;

	const usage = asNumber(parsed.scalars['usage']);
	if (usage !== undefined) initial.usage = usage;

	const orgUserCount = asNumber(parsed.scalars['orgUserCount']);
	if (orgUserCount !== undefined) initial.orgUserCount = orgUserCount;

	const products = buildProducts(parsed.arrays['products'], parsed.nested);
	if (products) initial.products = products;

	return initial;
};

const asEnum = <T extends string>(
	value: string | undefined,
	allowed: ReadonlySet<T>,
): T | undefined => (value && allowed.has(value as T) ? (value as T) : undefined);

const asNumber = (value: string | undefined): number | undefined => {
	if (value === undefined) return undefined;
	const n = Number(value);
	return Number.isFinite(n) ? n : undefined;
};

const EDITION_VALUES: ReadonlySet<Edition> = new Set<Edition>(EDITION_KEYS);

/**
 * Reconstruct a single product from its config default, overriding any
 * inputs present (and valid) in the query's nested `product.<key>.*` record.
 */
const productFromNested = (
	key: ProductKey,
	nested: Record<string, string> | undefined,
): Product => {
	const base = buildDefaultProduct(key);
	if (!nested) return base;
	const unitCount = asNumber(nested['unitCount']);
	const edition = asEnum<Edition>(nested['edition'], EDITION_VALUES) ?? base.inputs.edition;
	return {
		productKey: key,
		inputs: {
			edition,
			unitCount: unitCount ?? base.inputs.unitCount,
			billingCycle: resolveBillingCycle(key, edition),
		},
	};
};

const PRODUCT_KEY_VALUES: ReadonlySet<ProductKey> = new Set<ProductKey>(productPricingConfigKeys);

const buildProducts = (
	selected: string[] | undefined,
	nested: ParsedQuery['nested'],
): Product[] | undefined => {
	if (!selected) return undefined;
	const result: Product[] = [];
	const seen = new Set<ProductKey>();
	for (const raw of selected) {
		const key = asEnum<ProductKey>(raw, PRODUCT_KEY_VALUES);
		if (!key || seen.has(key)) continue;
		seen.add(key);
		result.push(productFromNested(key, nested[`product.${key}`]));
	}
	return result.length > 0 ? result : undefined;
};
